import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ContentCopy,
  Description,
  Lock,
} from '@mui/icons-material';
import {
  useAITemplates,
  useCreateAITemplate,
  useUpdateAITemplate,
  useDeleteAITemplate,
} from '../../hooks/useAIGeneration';
import type { AIPromptTemplate } from '../../api/types';
import { useOrgFilter } from '../../contexts/OrganizationContext';

const SONGFORGE_ACCENT_COLOR = '#8b5cf6';

interface TemplateCardProps {
  template: AIPromptTemplate;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: (content: string, type: string) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onEdit, onDelete, onCopy }) => {
  return (
    <Card sx={{ height: '100%', opacity: template.is_active ? 1 : 0.6 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h6" noWrap sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {template.name}
              {template.is_system && (
                <Tooltip title="System template (read-only)">
                  <Lock fontSize="small" color="action" />
                </Tooltip>
              )}
            </Typography>
            {!template.is_active && (
              <Chip label="Inactive" size="small" color="default" sx={{ mt: 0.5 }} />
            )}
          </Box>
          <Box>
            {!template.is_system && (
              <>
                <IconButton size="small" onClick={onEdit}>
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={onDelete}>
                  <Delete fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        </Box>

        {template.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {template.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {template.genre && <Chip label={template.genre} size="small" />}
          {template.mood && <Chip label={template.mood} size="small" variant="outlined" />}
          <Chip label={`Used ${template.usage_count}x`} size="small" variant="outlined" />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {template.claude_prompt_template && (
            <Tooltip title="Copy Claude template">
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={() => onCopy(template.claude_prompt_template!, 'Claude')}
              >
                Claude
              </Button>
            </Tooltip>
          )}
          {template.style_prompt_template && (
            <Tooltip title="Copy style template">
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={() => onCopy(template.style_prompt_template!, 'Style')}
              >
                Style
              </Button>
            </Tooltip>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const TemplatesSection: React.FC = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AIPromptTemplate | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const orgFilter = useOrgFilter();

  const { data: templates, isLoading, error } = useAITemplates({ organization_id: orgFilter });

  const createMutation = useCreateAITemplate();
  const updateMutation = useUpdateAITemplate();
  const deleteMutation = useDeleteAITemplate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    claude_prompt_template: '',
    style_prompt_template: '',
    genre: '',
    mood: '',
    is_active: true,
  });

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      description: '',
      claude_prompt_template: '',
      style_prompt_template: '',
      genre: '',
      mood: '',
      is_active: true,
    });
    setEditingTemplate(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (template: AIPromptTemplate) => {
    if (template.is_system) {
      setSnackbarMessage('System templates cannot be edited');
      setSnackbarOpen(true);
      return;
    }
    setFormData({
      name: template.name,
      description: template.description || '',
      claude_prompt_template: template.claude_prompt_template || '',
      style_prompt_template: template.style_prompt_template || '',
      genre: template.genre || '',
      mood: template.mood || '',
      is_active: template.is_active,
    });
    setEditingTemplate(template);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        organization_id: orgFilter || undefined,
        name: formData.name,
        description: formData.description || undefined,
        claude_prompt_template: formData.claude_prompt_template || undefined,
        style_prompt_template: formData.style_prompt_template || undefined,
        genre: formData.genre || undefined,
        mood: formData.mood || undefined,
      };

      if (editingTemplate) {
        await updateMutation.mutateAsync({
          id: editingTemplate.id,
          data: { ...data, is_active: formData.is_active },
        });
      } else {
        await createMutation.mutateAsync(data);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      console.error('Failed to save template:', err);
    }
  };

  const handleDelete = async (template: AIPromptTemplate) => {
    if (template.is_system) {
      setSnackbarMessage('System templates cannot be deleted');
      setSnackbarOpen(true);
      return;
    }
    if (window.confirm('Are you sure you want to delete this template?')) {
      await deleteMutation.mutateAsync(template.id);
    }
  };

  const handleCopyTemplate = (content: string, type: string) => {
    navigator.clipboard.writeText(content);
    setSnackbarMessage(`${type} template copied to clipboard`);
    setSnackbarOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load templates: {(error as Error)?.message}
      </Alert>
    );
  }

  const systemTemplates = templates?.filter((t) => t.is_system) || [];
  const customTemplates = templates?.filter((t) => !t.is_system) || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          sx={{ bgcolor: SONGFORGE_ACCENT_COLOR }}
        >
          Create Template
        </Button>
      </Box>

      {/* System Templates */}
      {systemTemplates.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Lock fontSize="small" />
            System Templates
          </Typography>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {systemTemplates.map((template) => (
              <Grid item xs={12} md={6} lg={4} key={template.id}>
                <TemplateCard
                  template={template}
                  onEdit={() => handleOpenEdit(template)}
                  onDelete={() => handleDelete(template)}
                  onCopy={handleCopyTemplate}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Custom Templates */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Custom Templates
      </Typography>
      <Grid container spacing={2}>
        {customTemplates.map((template) => (
          <Grid item xs={12} md={6} lg={4} key={template.id}>
            <TemplateCard
              template={template}
              onEdit={() => handleOpenEdit(template)}
              onDelete={() => handleDelete(template)}
              onCopy={handleCopyTemplate}
            />
          </Grid>
        ))}
        {customTemplates.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Description sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">
                No custom templates yet. Create templates to speed up your workflow.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Template Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What is this template for?"
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Target Genre (Optional)"
                value={formData.genre}
                onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                placeholder="e.g., Pop, Electronic"
                fullWidth
              />
              <TextField
                label="Target Mood (Optional)"
                value={formData.mood}
                onChange={(e) => setFormData({ ...formData, mood: e.target.value })}
                placeholder="e.g., Upbeat, Chill"
                fullWidth
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Claude Analysis Template
            </Typography>
            <TextField
              value={formData.claude_prompt_template}
              onChange={(e) => setFormData({ ...formData, claude_prompt_template: e.target.value })}
              multiline
              rows={6}
              placeholder="Template for Claude analysis prompt. Use {{title}}, {{artist}}, {{reason}} as placeholders."
              helperText="Available placeholders: {{title}}, {{artist}}, {{reason}}"
              fullWidth
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Style Prompt Template
            </Typography>
            <TextField
              value={formData.style_prompt_template}
              onChange={(e) => setFormData({ ...formData, style_prompt_template: e.target.value })}
              multiline
              rows={4}
              placeholder="Template for AI generation style prompt. Use {{mood}}, {{tempo}}, {{key}}, {{energy}}, {{bpm}} as placeholders."
              helperText="Available placeholders: {{mood}}, {{tempo}}, {{key}}, {{energy}}, {{bpm}}, {{instruments}}, {{style}}, {{subgenre}}, {{elements}}"
              fullWidth
            />

            {editingTemplate && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
          >
            {editingTemplate ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default TemplatesSection;
